'use client';

import { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross1Icon } from '@radix-ui/react-icons';
import { Button, IconButton, TextField, TextArea, Theme } from '@radix-ui/themes';
import { apiClient, type SupportCategory } from '@/lib/api-client';
import { useAuth } from '@/lib/auth-context';
import { useTenant } from '@/lib/tenant-context';
import { logger } from '@/lib/logger';

interface SupportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const SUBJECT_MAX = 200;
const MESSAGE_MAX = 10_000;

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function SupportDialog({ isOpen, onClose }: Readonly<SupportDialogProps>) {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const [category, setCategory] = useState<SupportCategory>('question');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  // Reset submission status when the dialog closes. React-documented
  // "store info from previous renders" pattern — setState during render
  // is fine when guarded by a comparison; satisfies
  // `react-hooks/set-state-in-effect` without an extra commit cycle.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setStatus('idle');
      setErrorMessage(null);
    }
  }

  const reset = () => {
    setSubject('');
    setMessage('');
    setCategory('question');
    setStatus('idle');
    setErrorMessage(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const canSubmit =
    status !== 'sending' &&
    subject.trim().length > 0 &&
    subject.length <= SUBJECT_MAX &&
    message.trim().length > 0 &&
    message.length <= MESSAGE_MAX &&
    !!tenantId;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus('sending');
    setErrorMessage(null);
    try {
      await apiClient.sendSupportRequest({
        tenant_id: tenantId,
        category,
        subject: subject.trim(),
        message: message.trim(),
      });
      setStatus('sent');
    } catch (err) {
      logger.error('[SupportDialog] sendSupportRequest failed', err);
      setStatus('error');
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to send your support request. Please try again or email support@foltrigg.ai.',
      );
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => (!open ? close() : undefined)}>
      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg w-[560px] max-w-[95vw] z-50 p-6"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              subjectRef.current?.focus();
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-xl font-semibold text-olive-9">
                Contact support
              </Dialog.Title>
              <Dialog.Close asChild>
                <IconButton variant="ghost" color="gray" size="1" aria-label="Close">
                  <Cross1Icon />
                </IconButton>
              </Dialog.Close>
            </div>
            <Dialog.Description className="text-sm text-olive-7 mb-4">
              Reach the Foltrigg support team. Your message goes to{' '}
              <strong>support@foltrigg.ai</strong>.
            </Dialog.Description>

            <div className="bg-yellow-2 border border-yellow-6 rounded-md p-3 mb-4 text-sm text-yellow-11">
              <strong>Heads-up:</strong> Do not paste case file IDs, suspect or victim
              names, or any other criminal-justice information into this form. We will
              reach back to you at <strong>{user?.email ?? 'your account email'}</strong>{' '}
              and can ask for further details over a secure channel.
            </div>

            {status === 'sent' ? (
              <div className="bg-green-2 border border-green-6 rounded-md p-4 text-sm text-green-11 mb-4">
                Message sent. We will be in touch at{' '}
                <strong>{user?.email ?? 'your account email'}</strong>.
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                className="space-y-3"
              >
                <div>
                  <label
                    htmlFor="support-category"
                    className="block text-xs font-medium text-olive-9 mb-1"
                  >
                    Category
                  </label>
                  <select
                    id="support-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as SupportCategory)}
                    disabled={status === 'sending'}
                    className="w-full h-8 px-2.5 border border-gray-300 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-olive-9 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="question">Question</option>
                    <option value="bug">Bug report</option>
                    <option value="feature">Feature request</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="support-subject"
                    className="block text-xs font-medium text-olive-9 mb-1"
                  >
                    Subject
                  </label>
                  <TextField.Root
                    id="support-subject"
                    ref={subjectRef}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={SUBJECT_MAX}
                    placeholder="Short summary"
                    disabled={status === 'sending'}
                  />
                  <p className="text-xs text-olive-6 text-right mt-1">
                    {subject.length} / {SUBJECT_MAX}
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="support-message"
                    className="block text-xs font-medium text-olive-9 mb-1"
                  >
                    Message
                  </label>
                  <TextArea
                    id="support-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={MESSAGE_MAX}
                    placeholder="Describe what you need help with"
                    className="h-[180px]"
                    disabled={status === 'sending'}
                  />
                  <p className="text-xs text-olive-6 text-right mt-1">
                    {message.length.toLocaleString()} / {MESSAGE_MAX.toLocaleString()}
                  </p>
                </div>

                {status === 'error' && errorMessage && (
                  <div
                    role="alert"
                    className="bg-red-2 border border-red-6 rounded-md p-3 text-sm text-red-11"
                  >
                    {errorMessage}
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={close}
                    className="text-sm font-medium text-blue-11 hover:underline"
                    disabled={status === 'sending'}
                  >
                    Cancel
                  </button>
                  <Button
                    type="submit"
                    variant="solid"
                    color="orange"
                    size="3"
                    disabled={!canSubmit}
                    aria-busy={status === 'sending'}
                  >
                    {status === 'sending' ? 'Sending…' : 'Send to support'}
                  </Button>
                </div>
              </form>
            )}
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
